/**
 * Разбор одной задачи (§45, §51).
 *
 * Здесь видно то, ради чего в админку и заходят с упавшей задачей: что именно
 * ответил провайдер. Плюс возврат единиц, если формально всё прошло,
 * а результат негодный.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { adminApi } from '../../api/admin';
import { Button } from '../../components/ui/Button';
import { Alert } from '../../components/ui/Alert';
import { useTranslation } from 'react-i18next';
import { errorText } from '../../i18n/errors';

const json = (value: unknown): string => JSON.stringify(value, null, 2);

export function AdminJobPage() {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');

  const job = useQuery({ queryKey: ['admin', 'job', id], queryFn: () => adminApi.job(id) });

  const refund = useMutation({
    mutationFn: () => adminApi.refundJob(id, reason || t('admin.job.defaultRefundReason')),
    onSuccess: () => {
      setReason('');
      void queryClient.invalidateQueries({ queryKey: ['admin', 'job', id] });
    },
  });

  const data = job.data;

  return (
    <div className="flex flex-col gap-4">
      <button
        type="button"
        onClick={() => navigate('/admin/jobs')}
        className="flex w-fit items-center gap-1.5 text-meta text-content-muted transition-colors duration-fast hover:text-content"
      >
        <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.75} />
        {t('admin.backToList')}
      </button>

      {data ? (
        <>
          <section className="grid grid-cols-2 gap-3 rounded-card border border-line bg-surface p-4 text-meta lg:grid-cols-4">
            <Field label={t('admin.job.operation')} value={data.operation} />
            <Field
              label={t('admin.job.status')}
              value={data.status}
              tone={data.status === 'failed' ? 'bad' : undefined}
            />
            <Field label={t('admin.job.error')} value={data.errorCode ?? '—'} />
            <Field label={t('admin.job.user')} value={data.userEmail} />
            <Field label={t('admin.job.reserved')} value={String(data.unitsReserved)} />
            <Field label={t('admin.job.spent')} value={String(data.unitsSpent)} />
            <Field label={t('admin.job.cost')} value={`$${(data.costCents / 100).toFixed(2)}`} />
            <Field label={t('admin.job.attempts')} value={String(data.attempts)} />
          </section>

          {data.unitsSpent > 0 ? (
            <section className="flex flex-col gap-2 rounded-card border border-line bg-surface p-4">
              <p className="text-meta text-content-secondary">{t('admin.job.refundTitle')}</p>
              <div className="flex flex-wrap gap-2">
                <input
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  placeholder={t('admin.job.reasonPlaceholder')}
                  className="h-9 flex-1 rounded-control border border-line bg-surface-secondary px-3 text-meta text-content placeholder:text-content-muted focus:border-accent focus:outline-none"
                />
                <Button
                  variant="secondary"
                  disabled={refund.isPending}
                  onClick={() => refund.mutate()}
                >
                  {refund.isPending
                    ? t('admin.job.refunding')
                    : t('admin.job.refundValue', { value: data.unitsSpent })}
                </Button>
              </div>
              {refund.isError ? <Alert tone="error">{errorText(refund.error)}</Alert> : null}
              {refund.isSuccess ? <Alert tone="success">{t('admin.job.refunded')}</Alert> : null}
            </section>
          ) : null}

          <section className="flex flex-col gap-3">
            <p className="text-meta text-content-secondary">{t('admin.job.attemptsTitle')}</p>
            {data.providerJobs.map((item) => (
              <div key={item.id} className="rounded-card border border-line bg-surface p-4">
                <div className="mb-2 flex flex-wrap gap-4 text-meta">
                  <span className="text-content">
                    {t('admin.job.attemptLine', {
                      attempt: item.attempt,
                      provider: item.provider,
                      kind: item.providerKind,
                    })}
                  </span>
                  {/* Модель рядом с провайдером: компания и то, что реально
                      сделало кадр, меняются порознь (§51). */}
                  {item.model ? <span className="text-content-secondary">{item.model}</span> : null}
                  <span
                    className={item.status === 'failed' ? 'text-error' : 'text-content-secondary'}
                  >
                    {item.status} {item.errorCode ?? ''}
                  </span>
                  <span className="text-content-secondary">
                    ${(item.costCents / 100).toFixed(2)}
                  </span>
                </div>
                <details>
                  <summary className="cursor-pointer text-meta text-content-muted">
                    {t('admin.job.requestResponse')}
                  </summary>
                  <pre className="mt-2 max-h-72 overflow-auto rounded-control bg-surface-secondary p-3 text-[11px] leading-4 text-content-secondary">
                    {json({ request: item.request, response: item.response })}
                  </pre>
                </details>
              </div>
            ))}
          </section>

          <details className="rounded-card border border-line bg-surface p-4">
            <summary className="cursor-pointer text-meta text-content-secondary">
              {t('admin.job.inputResult')}
            </summary>
            <pre className="mt-2 max-h-96 overflow-auto rounded-control bg-surface-secondary p-3 text-[11px] leading-4 text-content-secondary">
              {json({ input: data.input, result: data.result })}
            </pre>
          </details>
        </>
      ) : (
        <p className="text-meta text-content-muted">{t('common.loading')}</p>
      )}
    </div>
  );
}

function Field({ label, value, tone }: { label: string; value: string; tone?: 'bad' }) {
  return (
    <div>
      <p className="text-content-muted">{label}</p>
      <p className={tone === 'bad' ? 'text-error' : 'text-content'}>{value}</p>
    </div>
  );
}
